import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Fuse from 'fuse.js';
import { Search } from 'lucide-react';

import {
  INSTRUCTIONS,
  FETCHES,
  EVENTS,
  type Instruction,
  type FetchEntry,
  type EventEntry,
} from '../../data/instructions.generated';
import { useAdminMode } from '../../lib/admin-mode-context';

type Item =
  | { kind: 'instruction'; name: string; group: string; description: string; isAdmin: boolean }
  | { kind: 'fetch'; name: string; group: string; description: string; isAdmin: false }
  | { kind: 'event'; name: string; group: string; description: string; isAdmin: false }
  | { kind: 'page'; name: string; group: string; description: string; isAdmin: false };

const STATIC_PAGES: Extract<Item, { kind: 'page' }>[] = [
  {
    kind: 'page',
    name: 'quickstart',
    group: 'docs',
    description: 'Install, configure, build an instruction, and Try-it.',
    isAdmin: false,
  },
  {
    kind: 'page',
    name: 'events-listener',
    group: 'docs',
    description: 'Decode SRSLY events with the SDK — no Anchor, no IDL.',
    isAdmin: false,
  },
];

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onPick: (name: string) => void;
}

type Range = readonly [number, number];

// Find every literal occurrence of each query token in `text`. We deliberately
// do NOT use Fuse's per-character `indices` for highlighting — those light up
// individual letters of fuzzy matches (e.g. "close" matches the c/l/o/s/e in
// "automation thread"), which is visually noisy. Whole-token substring
// highlights show only the part the user actually typed.
function literalRanges(text: string, query: string): Range[] {
  const q = query.trim();
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  const lower = text.toLowerCase();
  const ranges: Range[] = [];
  for (const token of tokens) {
    const needle = token.toLowerCase();
    let from = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const at = lower.indexOf(needle, from);
      if (at === -1) break;
      ranges.push([at, at + needle.length - 1]);
      from = at + needle.length;
    }
  }
  // Merge any overlap so adjacent/duplicate hits render as one <mark>.
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Range[] = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1] + 1) {
      merged[merged.length - 1] = [last[0], Math.max(last[1], r[1])];
    } else {
      merged.push(r);
    }
  }
  return merged;
}

function highlight(text: string, ranges: Range[]): ReactNode {
  if (ranges.length === 0) return text;
  const out: ReactNode[] = [];
  let cursor = 0;
  ranges.forEach(([start, end], i) => {
    if (start > cursor) out.push(text.slice(cursor, start));
    out.push(
      <mark key={`m${i}-${start}`} className="cp-mark">
        {text.slice(start, end + 1)}
      </mark>
    );
    cursor = end + 1;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

// Description bodies can be paragraphs long. Window the visible excerpt
// around the first literal match so the highlight stays inside the 2-line
// clamp. When the row matched only via fuzzy ranking (no literal hit), fall
// back to a plain prefix.
function highlightWindow(text: string, ranges: Range[]): ReactNode {
  if (ranges.length === 0) {
    return text.length > 160 ? text.slice(0, 160) + '…' : text;
  }
  const WINDOW = 140;
  const first = ranges[0];
  let start = Math.max(0, first[0] - 30);
  let end = Math.min(text.length, start + WINDOW);
  if (start > 0) {
    const space = text.indexOf(' ', start);
    if (space !== -1 && space - start < 20) start = space + 1;
  }
  if (end < text.length) {
    const space = text.lastIndexOf(' ', end);
    if (space > start + 60) end = space;
  }
  const slice = text.slice(start, end);
  const shifted: Range[] = [];
  for (const [s, e] of ranges) {
    if (e < start || s >= end) continue;
    shifted.push([Math.max(0, s - start), Math.min(slice.length - 1, e - start)]);
  }
  const prefix = start > 0 ? '…' : '';
  const suffix = end < text.length ? '…' : '';
  return (
    <>
      {prefix}
      {highlight(slice, shifted)}
      {suffix}
    </>
  );
}

function buildItems(adminEnabled: boolean): Item[] {
  const items: Item[] = [...STATIC_PAGES];
  for (const ix of INSTRUCTIONS as Instruction[]) {
    if (ix.isAdmin && !adminEnabled) continue;
    items.push({
      kind: 'instruction',
      name: ix.name,
      group: ix.group,
      description: ix.description,
      isAdmin: ix.isAdmin,
    });
  }
  for (const f of FETCHES as FetchEntry[]) {
    items.push({
      kind: 'fetch',
      name: f.name,
      group: f.group,
      description: f.description,
      isAdmin: false,
    });
  }
  for (const e of EVENTS as EventEntry[]) {
    items.push({
      kind: 'event',
      name: e.name,
      // Synthesize a search-friendly group label like `events.rental.accepted`
      // so typing either the dot-form or the IDL name surfaces the row.
      group: `events.${e.group}.${e.verb}`,
      description: e.description,
      isAdmin: false,
    });
  }
  return items;
}

export function CommandPalette({ open, onClose, onPick }: CommandPaletteProps) {
  const { enabled: adminEnabled } = useAdminMode();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Rebuild when admin toggle flips so admin instructions appear/disappear
  // without a remount.
  const items = useMemo(() => buildItems(adminEnabled), [adminEnabled]);

  // Search tuning:
  //   - threshold 0.25 only forgives a typo or two (createCntract still finds
  //     createContract). Higher values bled in long-description fuzzy matches
  //     like estimateRentalCost showing up for "close".
  //   - minMatchCharLength 3 stops 1- and 2-char tokens from spraying noise
  //     across descriptions.
  //   - name carries most of the signal; description is a tiebreaker, group
  //     barely contributes.
  const fuse = useMemo(
    () =>
      new Fuse(items, {
        keys: [
          { name: 'name', weight: 0.75 },
          { name: 'description', weight: 0.2 },
          { name: 'group', weight: 0.05 },
        ],
        threshold: 0.25,
        ignoreLocation: true,
        minMatchCharLength: 3,
      }),
    [items]
  );

  type Result = { item: Item };
  const results = useMemo<Result[]>(() => {
    const q = query.trim();
    if (!q) return items.slice(0, 30).map(item => ({ item }));
    return fuse.search(q, { limit: 30 }).map(r => ({ item: r.item }));
  }, [fuse, query, items]);

  // Reset query/selection on each open so the user starts fresh.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // Defer focus so the input is mounted by the time we focus it.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Scroll the active row into view as the selection moves.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cp-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  // Clamp the selection if results shrink under us.
  useEffect(() => {
    if (activeIdx >= results.length) setActiveIdx(Math.max(0, results.length - 1));
  }, [activeIdx, results.length]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = results[activeIdx];
      if (pick) {
        onPick(pick.item.name);
        onClose();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="cp-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Search playground"
      onMouseDown={e => {
        // Click on the backdrop (not the card) closes.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cp-card">
        <div className="cp-input-row">
          <Search size={14} strokeWidth={1.6} className="cp-input-icon" />
          <input
            ref={inputRef}
            className="cp-input"
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Search instructions, fetches, groups…"
            spellCheck={false}
            autoComplete="off"
          />
          <kbd className="cp-kbd">esc</kbd>
        </div>
        <div className="cp-list" ref={listRef}>
          {results.length === 0 && (
            <div className="cp-empty">No matches for &ldquo;{query}&rdquo;.</div>
          )}
          {results.map(({ item }, i) => {
            const nameRanges = literalRanges(item.name, query);
            const descRanges = literalRanges(item.description, query);
            return (
              <button
                key={`${item.kind}:${item.name}`}
                type="button"
                data-cp-idx={i}
                className={`cp-row${i === activeIdx ? ' is-active' : ''}`}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => {
                  onPick(item.name);
                  onClose();
                }}
              >
                <span className={`cp-kind cp-kind-${item.kind}`}>{item.kind}</span>
                <span className="cp-row-name">{highlight(item.name, nameRanges)}</span>
                <span className="cp-row-group">{item.group}</span>
                {item.kind === 'instruction' && item.isAdmin && (
                  <span className="cp-row-badge">admin</span>
                )}
                {item.description && (
                  <span className="cp-row-desc">
                    {highlightWindow(item.description, descRanges)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="cp-foot">
          <span>
            <kbd className="cp-kbd">↑</kbd>
            <kbd className="cp-kbd">↓</kbd>
            navigate
          </span>
          <span>
            <kbd className="cp-kbd">↵</kbd>
            open
          </span>
          <span style={{ marginLeft: 'auto' }}>
            {results.length} of {items.length}
          </span>
        </div>
      </div>
    </div>
  );
}
